/**
 * Cancel a count — D-6, and the one destructive-looking action on this screen.
 *
 * **The responsible manager only, from `Open` or `InReview` only.** That is not a
 * UI preference: `ValidateApiTransition` permits those two transitions and nothing
 * else, and the backend enforces ownership with no admin bypass. This component
 * offers the action only where the server would accept it, so a manager does not
 * discover the rule as a 403 or a 409.
 *
 * ⚠ **The confirm says the lines are FROZEN, not deleted, and that wording is the
 * whole point of this file.**
 *
 * D-6's recommendation said the confirm would tell the manager their staged lines
 * would be deleted. That turned out to be wrong, and it was found by building it:
 * `LSCountLine.Table.al` **freezes** a cancelled session's lines. They stay in
 * staging, they stay readable, and this app exposes no delete path at all — only
 * `verify_count_flow.py` deletes, to clean up after itself. Proven live at D0 and
 * again at D5.
 *
 * So a dialog promising the numbers will be gone would be lying about the one thing
 * the manager is being asked to authorise. That is the kind of wrong nobody catches
 * until a manager goes looking for numbers they were told had been thrown away —
 * or, worse, cancels *in order to* delete them and finds them still there.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "../api/client";
import { useCancelSession } from "../api/queries";
import type { CountSession } from "../api/types";

/** The only two statuses the AL will cancel from. */
const CANCELLABLE = ["Open", "InReview"];

export function CancelSession({
  session,
  areaId,
  mine,
}: {
  session: CountSession;
  areaId: string;
  mine: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancel = useCancelSession(areaId, session.session_id);
  const navigate = useNavigate();

  // Not rendered at all where the server would refuse it. An `Error` session in
  // particular cannot be cancelled — the AL has no `Error -> Cancelled` transition
  // — and offering the button there would promise a way out that does not exist.
  if (!mine || !CANCELLABLE.includes(session.status)) return null;

  return (
    <div className="card" data-testid="cancel-panel">
      <h3>Cancel this count</h3>
      {!confirming ? (
        <>
          <p className="small">
            Closes the count without posting anything to Business Central.
          </p>
          <button
            type="button"
            className="danger"
            onClick={() => setConfirming(true)}
            data-testid="cancel-session"
          >
            Cancel this count
          </button>
        </>
      ) : (
        <div className="confirm" data-testid="cancel-confirm">
          <p>
            <strong>Close this count without posting?</strong>
          </p>
          <p className="small">
            Nothing is posted to Business Central and no inventory moves. The{" "}
            {session.line_count} counted line{session.line_count === 1 ? "" : "s"} already
            staged{" "}
            <strong>stay in Business Central and stay readable</strong> — cancelling
            freezes them, it does not delete them. A cancelled count cannot be
            reopened; counting this area again means starting a new count.
          </p>
          {error && (
            <p className="error-text small" data-testid="cancel-error">
              {error}
            </p>
          )}
          <div className="confirm__buttons">
            <button
              type="button"
              className="danger"
              disabled={cancel.isPending}
              data-testid="cancel-confirm-yes"
              onClick={async () => {
                setError(null);
                try {
                  await cancel.mutateAsync();
                  navigate(`/areas/${encodeURIComponent(areaId)}`);
                } catch (err) {
                  // The server's own words. It explains, for instance, that an
                  // `Error` session has no cancel transition and what to do instead.
                  setError(
                    err instanceof ApiError
                      ? err.detail
                      : `Could not cancel this count: ${(err as Error).message}`,
                  );
                }
              }}
            >
              {cancel.isPending ? "Cancelling…" : "Yes, close this count"}
            </button>
            <button type="button" className="ghost" onClick={() => setConfirming(false)}>
              Keep counting
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
